import { useMemo } from 'react';

// porp-types is a library for typechecking of props
import PropTypes from 'prop-types';

// @mui/x-charts components
import { LineChart } from '@mui/x-charts/LineChart';

// @mui material components
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

function ReportsLineChart({ color, title, description, date, chart }) {
    const series = [
        {
            data: chart.datasets?.data || [],
            label: chart.datasets?.label || '',
            curve: 'linear',
            color: 'rgba(255, 255, 255, .8)',
        },
    ];

    return (
        <Card sx={{ height: '100%' }}>
            <MDBox padding="1rem">
                {useMemo(
                    () => (
                        <MDBox
                            variant="gradient"
                            bgColor={color}
                            borderRadius="lg"
                            coloredShadow={color}
                            py={2}
                            pr={0.5}
                            mt={-5}
                            height="12.5rem"
                        >
                            <LineChart
                                series={series}
                                xAxis={[
                                    {
                                        data: chart.labels || [],
                                        scaleType: 'point',
                                        tickLabelStyle: {
                                            color: '#f8f9fa',
                                            fontSize: 14,
                                            fontFamily: 'Roboto',
                                        },
                                        gridStyle: {
                                            display: false,
                                        },
                                    },
                                ]}
                                yAxis={[
                                    {
                                        tickLabelStyle: {
                                            color: '#f8f9fa',
                                            fontSize: 14,
                                            fontFamily: 'Roboto',
                                        },
                                        gridStyle: {
                                            strokeDasharray: '5 5',
                                            stroke: 'rgba(255, 255, 255, .2)',
                                        },
                                    },
                                ]}
                                height={220}
                                margin={{ left: 40, right: 10, top: 10, bottom: 30 }}
                                slotProps={{
                                    legend: { hidden: true },
                                }}
                                sx={{
                                    '.MuiLineElement-root': {
                                        strokeWidth: 4,
                                    },
                                    '.MuiMarkElement-root': {
                                        stroke: 'transparent',
                                        fill: 'rgba(255, 255, 255, .8)',
                                        r: 5,
                                    },
                                }}
                            />
                        </MDBox>
                    ),
                    [chart, color]
                )}
                <MDBox pt={3} pb={1} px={1}>
                    <MDTypography variant="h6" textTransform="capitalize">
                        {title}
                    </MDTypography>
                    <MDTypography component="div" variant="button" color="text" fontWeight="light">
                        {description}
                    </MDTypography>
                    <Divider />
                    <MDBox display="flex" alignItems="center">
                        <MDTypography variant="button" color="text" lineHeight={1} sx={{ mt: 0.15, mr: 0.5 }}>
                            <Icon>schedule</Icon>
                        </MDTypography>
                        <MDTypography variant="button" color="text" fontWeight="light">
                            {date}
                        </MDTypography>
                    </MDBox>
                </MDBox>
            </MDBox>
        </Card>
    );
}

// Setting default values for the props of ReportsLineChart
ReportsLineChart.defaultProps = {
    color: 'dark',
    description: '',
};

// Typechecking props for the ReportsLineChart
ReportsLineChart.propTypes = {
    color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error', 'dark']),
    title: PropTypes.string.isRequired,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    date: PropTypes.string.isRequired,
    chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
};

export default ReportsLineChart;
